import { z } from 'zod';

/**
 * Mirrors apps/api/src/modules/identity/http/auth.dto.ts exactly — same bounds, same field names — so a validation
 * error surfaces to the merchant before a round trip, not after.
 *
 * Error messages are i18n KEYS (`auth.validation.*`), translated where they are rendered (`translateFieldError`), so the
 * same schema serves French, Arabic and English.
 */
const EMAIL_INVALID = 'auth.validation.email';
const PASSWORD_SHORT = 'auth.validation.passwordShort';
const CHOSEN_PASSWORD_SHORT = 'auth.validation.chosenPasswordShort';
const PASSWORDS_MISMATCH = 'auth.validation.passwordsMismatch';

const email = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, EMAIL_INVALID)
  .max(254, EMAIL_INVALID)
  .email(EMAIL_INVALID);
const password = z.string().min(8, PASSWORD_SHORT).max(1024, PASSWORD_SHORT);
/**
 * A password CHOSEN here (registration, reset) is at least 12 characters (decision of 8 October 2026); a password PRESENTED at sign-in
 * keeps the old floor of 8, so an account created under the earlier rule can still sign in.
 */
const chosenPassword = z.string().min(12, CHOSEN_PASSWORD_SHORT).max(1024, CHOSEN_PASSWORD_SHORT);

export const loginFormSchema = z.object({ email, password });
export type LoginFormValues = z.infer<typeof loginFormSchema>;

export const forgotPasswordFormSchema = z.object({ email });
export type ForgotPasswordFormValues = z.infer<typeof forgotPasswordFormSchema>;

export const resetPasswordFormSchema = z
  .object({
    password: chosenPassword,
    confirmPassword: z.string().min(1, CHOSEN_PASSWORD_SHORT).max(1024, CHOSEN_PASSWORD_SHORT),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: PASSWORDS_MISMATCH,
    path: ['confirmPassword'],
  });
export type ResetPasswordFormValues = z.infer<typeof resetPasswordFormSchema>;
